/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { Injectable } from '@angular/core';


@Injectable({
  providedIn: 'root'
})
export class SessionUserV2Service {

  private _sessionId: string | null = null;
  private _districtId: string | null = null;
  private _collectionId: string | null = null;
  private _schoolId: string | null = null;
  private _gatewayUrl: string | null = null;
  private _userEmail: string | null = null;
  private _startPercent = 1;
  private _origin : string | null = null;
  private _redirectUrl: string | null = null;
  private _tabId: string | null = null;

  private _hasLoggedIn: boolean | null = null;

  // VLAB-778 / AC2 telemetry: measure the "session ready -> view connected" window and how much of
  // it the tab was hidden. Kept in this root singleton so it survives the navigation from the start
  // wizard to the istreamer view and keeps accumulating no matter which component is mounted.
  // Observability only: every method below is guarded and can never disrupt the session launch.
  private _readyAt = 0;
  private _hiddenMs = 0;
  private _hiddenSince = 0;
  private _visibilityHandler: (() => void) | null = null;

  get tabId(): string | null {
    if (this._tabId == null) {
      const storedValue = localStorage.getItem('tabId');
      this._tabId = storedValue ?? '';
    }
    return this._tabId;
 }

  set tabId(value: string | null) {
    this._tabId = value;
    const tabIdStr = value ?? '';
    localStorage.setItem('tabId', tabIdStr);
  }

 get hasLoggedIn(): boolean {
  if (this._hasLoggedIn == null) {
    const storedValue = sessionStorage.getItem('hasLoggedIn');
    this._hasLoggedIn = storedValue === 'true';
  }
  return this._hasLoggedIn;
}

set hasLoggedIn(value: boolean) {
  this._hasLoggedIn = value;
  sessionStorage.setItem('hasLoggedIn', value ? 'true' : 'false');
}

 get redirectUrl(): string | null {
  if (this._redirectUrl == null) {
    const storedValue = sessionStorage.getItem('redirectUrl');
    this._redirectUrl = storedValue ?? '';
  }
  return this._redirectUrl;
}

set redirectUrl(value: string | null) {
  this._redirectUrl = value;
  const redirectUrlStr = value ?? '';
  sessionStorage.setItem('redirectUrl', redirectUrlStr);
}

   // --- origin ---
 get origin(): string | null {
    if (this._origin == null) {
      const storedValue = sessionStorage.getItem('origin');
      this._origin = storedValue??'';
    }
    return this._origin;
  }

  set origin(value: string | null) {
    this._origin = value;
    const originStr = value ?? '';
    sessionStorage.setItem('origin', originStr);
  }

   // --- start Percent ---
  get startPercent(): number {
    if (this._startPercent == 1) {
      const storedValue = localStorage.getItem('startPercent');
      this._startPercent = storedValue ? Number(storedValue) : 1;
    }
    return this._startPercent;
  }

  set startPercent(value: number) {
    this._startPercent = value;
    localStorage.setItem('startPercent', value.toString());
  }

   // --- schoolId ---
  get schoolId(): string | null {
    if (!this._schoolId) {
      this._schoolId = sessionStorage.getItem('schoolId');
    }
    return this._schoolId;
  }
  set schoolId(value: string | null) {
    this._schoolId = value;
    const schoolIdStr = value ?? '';
    sessionStorage.setItem('schoolId', schoolIdStr);
  }

  // --- sessionId ---
  get sessionId(): string | null {
    if (!this._sessionId) {
      this._sessionId = sessionStorage.getItem('sessionId');
    }
    return this._sessionId;
  }
  set sessionId(value: string | null) {
    this._sessionId = value;
    const sessionIdStr = value ?? '';
    sessionStorage.setItem('sessionId', sessionIdStr);
  }

  // --- districtId ---
  get districtId(): string | null {
    if (!this._districtId) {
      this._districtId = sessionStorage.getItem('lastDistrict');
    }
    return this._districtId;
  }
  set districtId(value: string | null) {
    this._districtId = value;
    const districtIdStr = value ?? '';
    sessionStorage.setItem('lastDistrict', districtIdStr);
  }

  // --- collectionId ---
  get collectionId(): string | null {
    if (!this._collectionId) {
      this._collectionId = sessionStorage.getItem('collectionId');
    }
    return this._collectionId;
  }
  set collectionId(value: string | null) {
    this._collectionId = value;
    const collectionIdStr = value ?? '';
    sessionStorage.setItem('collectionId', collectionIdStr);
  }

  // --- schoolId ---
  get gatewayUrl(): string | null {
    if (!this._gatewayUrl) {
      this._gatewayUrl = sessionStorage.getItem('gatewayUrl');
    }
    return this._gatewayUrl;
  }

  set gatewayUrl(value: string | null) {
    this._gatewayUrl = value;
    const gatewayUrlStr = value ?? '';
    sessionStorage.setItem('gatewayUrl', gatewayUrlStr);
  }

  // --- userEmail ---
  get userEmail(): string | null {
    if (this._userEmail) {
        return this._userEmail;
    }

    const u = sessionStorage.getItem('user');
    const userObj = u ? JSON.parse(sessionStorage.getItem('user') as string): {};

    this._userEmail = userObj?.email ?? '';
    return this._userEmail;

  }

 set userEmail(value: string | null) {
    this._userEmail = value;
  }

  // --- AC2 telemetry: "session ready -> view connected" window (observability only) ---
  /**
   * Marks the moment the session became ready (status Running). Idempotent per session: the first
   * call stamps the timestamp and starts accumulating hidden time; later calls are ignored.
   * Guarded so it can never throw into the launch path.
   */
  markSessionReady(): void {
    try {
      if (this._readyAt) { return; }
      this._readyAt = Date.now();
      this._hiddenMs = 0;
      this._hiddenSince = document.hidden ? this._readyAt : 0;
      this._visibilityHandler = (): void => {
        try {
          if (document.hidden) {
            this._hiddenSince = Date.now();
          } else if (this._hiddenSince) {
            this._hiddenMs += Date.now() - this._hiddenSince;
            this._hiddenSince = 0;
          }
        } catch { /* telemetry only: never disrupt the session */ }
      };
      document.addEventListener('visibilitychange', this._visibilityHandler);
    } catch {
      // Telemetry only: on any failure leave tracking disabled; the launch is unaffected.
      this._readyAt = 0;
    }
  }

  /**
   * Returns { msReadyToView, hiddenMs } for the window from markSessionReady() to now (called when
   * the istreamer view connects), then stops tracking. Returns null if the ready moment was never
   * stamped or anything goes wrong — the caller treats null as "nothing to report".
   */
  consumeReadyToViewMetrics(): { msReadyToView: number; hiddenMs: number } | null {
    try {
      if (!this._readyAt) { return null; }
      const now = Date.now();
      let hiddenMs = this._hiddenMs;
      if (this._hiddenSince) {
        hiddenMs += now - this._hiddenSince;
      }
      const msReadyToView = now - this._readyAt;
      this.stopReadyToViewTracking();
      return { msReadyToView, hiddenMs };
    } catch {
      this.stopReadyToViewTracking();
      return null;
    }
  }

  private stopReadyToViewTracking(): void {
    try {
      if (this._visibilityHandler) {
        document.removeEventListener('visibilitychange', this._visibilityHandler);
      }
    } catch { /* ignore */ }
    this._visibilityHandler = null;
    this._readyAt = 0;
    this._hiddenMs = 0;
    this._hiddenSince = 0;
  }

  // --- clear ALL session data ---
  clearSession(): void {
    this._sessionId = null;
    this._districtId = null;
    this._collectionId = null;
    this._gatewayUrl = null;
    this._userEmail = null;
    this._origin = null;
    this._redirectUrl == null;
    this._hasLoggedIn = null;
    this.stopReadyToViewTracking();

    sessionStorage.removeItem('sessionId');
    sessionStorage.removeItem('lastDistrict');
    sessionStorage.removeItem('collectionId');
    localStorage.removeItem('startPercent');
    sessionStorage.removeItem('gatewayUrl');
    sessionStorage.removeItem('origin');
    sessionStorage.removeItem('redirectUrl');
    sessionStorage.removeItem('hasLoggedIn');
  }

  // --- clear PARTIAL session (keep districtId & userEmail) ---
  clearPartialSession(): void {
    this._sessionId = null;
    this._collectionId = null;
    this._gatewayUrl = null;
    this._startPercent = 1;
    this._tabId = null;
    this.stopReadyToViewTracking();

    sessionStorage.removeItem('sessionId');
    sessionStorage.removeItem('collectionId');
    localStorage.removeItem('startPercent');
    sessionStorage.removeItem('gatewayUrl');
    localStorage.removeItem('tabId');
    window.name = '';
  }

  generateUniqueId(): string {
    return Math.floor(Math.random() * 65536).toString();
  }

}
